// One square by the keyboard (issue 1175; design/12 §4): the ONE rule for a single token's step, used by the board's
// arrow keys and by the Tokens list, so both send exactly the `object.move` a drag sends and refuse in the same words.
// The screen's refusals come first (yours to move, the turn, a wall this screen holds); the server's are the protection.
import {
  WALL_IN_THE_WAY, previewRefusal, routeBlocked, stepCell,
  type EventBody, type Grid, type PlayerEncounter, type Point, type Size, type Wall,
} from '@dragons-table/vtt-shared';
import { moveRev } from './group';

export interface StepThing { id: string; kind: string; name?: string; x: number; y: number; size: Size; locked: boolean; rev?: number; mine?: boolean }

export interface StepWorld {
  grid: Grid;
  extent: { width: number; height: number };
  /** The walls this screen holds: the DM's; a player holds none and is told by the server. */
  walls: readonly Wall[];
  isDm: boolean;
  /** A co-DM with the tokens power (issue 1159) moves anything. */
  movesAny: boolean;
  /** The DM placing the table (issue 811): a step lands with no route, and walls do not hold. */
  placing: boolean;
  /** This viewer's reading of the fight, for the turn's refusal. */
  encounter: Pick<PlayerEncounter, 'running' | 'active' | 'combatants'> | null;
}

export const notYours = (name: string | undefined): string => `${name ?? 'That token'} is not yours to move.`;

/** Whether this viewer may move a thing at all (before the turn and the walls). */
export const mayStep = (o: StepThing, w: Pick<StepWorld, 'isDm' | 'movesAny'>): boolean => w.isDm || w.movesAny || o.mine === true;

/** The move one square along, or the screen's refusal; null for a locked thing (it never moves, and the board says nothing). */
export function keyStep(o: StepThing, di: number, dj: number, w: StepWorld): { body: EventBody; to: Point } | { say: string } | null {
  if (o.locked) return null;
  if (!mayStep(o, w)) return { say: notYours(o.name) };
  const refusal = w.encounter ? previewRefusal(w.encounter, o.id, w.isDm || w.movesAny ? 'dm' : 'player') : null;
  if (refusal) return { say: refusal };
  const to = stepCell(w.grid, w.extent, { x: o.x, y: o.y }, o.size, di, dj);
  if (to.x === o.x && to.y === o.y) return { say: 'The edge of the board is in the way.' };
  if (w.walls.length && !(w.isDm && w.placing) && routeBlocked({ x: o.x, y: o.y }, [to], w.walls)) return { say: WALL_IN_THE_WAY };
  return { body: { kind: 'object.move', id: o.id, route: w.placing ? [to] : [{ x: o.x, y: o.y }, to], rev: moveRev(o) } as EventBody, to };
}

/** Where a thing stands, in squares (the shared rule, so the history and the rewind list say it the same way). */
export { squareOf, squareWords } from '@dragons-table/vtt-shared';
